import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import Badge from "../../components/ui-components/Badge";

// Access-level wording for the Organisation roles table: coarser than the
// raw risk tiers so the column reads in plain language — elevated and high
// both surface as "Elevated"; a "critical" tier (not seeded today, but the
// column derives from `risk` rather than hardcoding the current 3 values)
// surfaces as "Critical". Unrecognized/missing risk falls back to Standard.
const ACCESS_LEVEL_BY_RISK = {
  standard: "Standard",
  elevated: "Elevated",
  high: "Elevated",
  critical: "Critical",
};
const ACCESS_LEVEL_TONE = { Standard: "success", Elevated: "warning", Critical: "danger" };

// Contextual recommendation copy (Sub-project E4, Task 5) — replaces the old
// blanket "Recommended" badge every template used to carry. The backend's
// enriched /roles listing now tags each template with zero or more
// situational reasons (`recommended_for`, see authz.RecommendedFor); a
// template with none of these tags shows no chip at all rather than falling
// back to a generic "Recommended".
//
// Platform cards only. The template cards below used to carry these too, but
// sentence-long copy like "Recommended for initial finance setup" told an
// admin the role was endorsed without telling them what kind of role it is;
// they now carry the two-axis classification (see CLASSIFICATION_BY_CODE)
// instead. The platform set is three roles, none of which reads as a
// "template you might pick", so the situational phrasing still earns its
// place there.
const RECOMMENDATION_COPY = {
  initial_finance_setup: "Recommended for initial finance setup",
  budget_owners: "Recommended for budget owners",
  least_privilege_start: "Least-privilege starting point",
  tenant_owner_alternative: "Recommended alternative to Tenant Owner",
};

// A role can carry more than one tag (e.g. both "least_privilege_start" and
// "budget_owners") — showing every chip would crowd the card, so this caps
// at the two most relevant, in RECOMMENDATION_COPY's declared priority
// order, dropping any tag the frontend doesn't recognize.
function recommendationChips(recommendedFor) {
  return (recommendedFor || [])
    .filter((tag) => RECOMMENDATION_COPY[tag])
    .sort((a, b) => Object.keys(RECOMMENDATION_COPY).indexOf(a) - Object.keys(RECOMMENDATION_COPY).indexOf(b))
    .slice(0, 2);
}

// Who each template is meant for — the one piece of "should I pick this one?"
// signal the seeded role data doesn't carry (the backend has name, description,
// purpose and risk, none of which name an audience). Keyed by role code rather
// than fetched: the template set is the fixed, code-seeded list in the
// backend's authz.SystemRoleTypes, so this can't drift onto tenant-authored
// roles — only system templates reach this card. A code with no entry renders
// no audience rather than a placeholder.
const AUDIENCE_BY_CODE = {
  finance_admin: "Finance operations",
  finance_manager: "Finance leadership",
  cost_owner: "Budget owners",
  tech_cost_owner: "Engineering & platform",
  exec_viewer: "Senior leadership",
  auditor: "Audit & compliance",
  viewer: "Wider organisation",
};

export function deriveAccessLevel(risk) {
  return ACCESS_LEVEL_BY_RISK[risk] || "Standard";
}

// Badge props for the derived access level — used by the Organisation
// roles table (RolesCatalogPage) so risk reads consistently there and on
// the platform cards below.
export function accessLevelBadge(risk) {
  const label = deriveAccessLevel(risk);
  return { status: ACCESS_LEVEL_TONE[label] || "success", label };
}

// The template card's one classification, on two axes: how central the role
// is to a normal setup (Core / Specialist / Optional) and how much access it
// carries (Elevated / Standard / Read-only). Together they answer "what kind
// of role is this?" in four words, which neither axis does alone — "Elevated"
// says nothing about whether you need it, "Core" says nothing about the risk
// you take on by handing it out.
//
// Keyed by code, alongside AUDIENCE_BY_CODE, and for the same reason: neither
// axis is derivable from what the backend seeds. Read-only in particular is
// invisible to `risk` — exec_viewer, auditor and viewer are all seeded
// `standard`, indistinguishable from cost_owner, which writes. A code with no
// entry falls back to the risk-derived access level alone.
const CLASSIFICATION_BY_CODE = {
  finance_admin: { tier: "Core", access: "Elevated" },
  finance_manager: { tier: "Core", access: "Elevated" },
  cost_owner: { tier: "Core", access: "Standard" },
  exec_viewer: { tier: "Core", access: "Read-only" },
  tech_cost_owner: { tier: "Specialist", access: "Standard" },
  auditor: { tier: "Specialist", access: "Read-only" },
  viewer: { tier: "Optional", access: "Read-only" },
};

// One pill can only carry one colour, so it encodes the access axis — the one
// with a safety consequence. The tier reads from the text.
const CLASSIFICATION_TONE = { ...ACCESS_LEVEL_TONE, "Read-only": "neutral" };

export function templateClassificationBadge(role) {
  const known = CLASSIFICATION_BY_CODE[role.code];
  const access = known ? known.access : deriveAccessLevel(role.risk);
  return {
    status: CLASSIFICATION_TONE[access] || "success",
    label: known ? `${known.tier} · ${access}` : `${access} access`,
  };
}

// The tier half of the classification, for callers that need to *act* on it
// rather than display it — the template picker offers Core templates as
// "recommended". Exported as a function rather than the map so the map stays
// the single source of truth. Returns undefined for any code the frontend
// doesn't classify, which callers treat as not-recommended.
export function templateTier(code) {
  return CLASSIFICATION_BY_CODE[code]?.tier;
}

// Friendly labels for the capability catalog's `group` field — shared by the
// template card's permission-footprint summary and the full grouped list in
// TemplatePickerSheet (via RoleCapabilityGroups), so both agree on how a raw
// group key reads. Any group the frontend doesn't recognize (or a capability
// missing from the catalog entirely, e.g. still loading) falls back to
// "Other" rather than disappearing.
export const GROUP_LABELS = {
  audit: "Audit",
  budget: "Budgeting",
  config: "Configuration",
  data: "Data",
  finance: "Finance",
  identity: "Identity & access",
  investigation: "Investigations",
  tenant: "Tenant",
};

export function groupLabel(rawGroup) {
  return GROUP_LABELS[rawGroup] || "Other";
}

// Buckets a role's capability codes by friendly group label (merging any raw
// group keys — including missing/unrecognized ones — that resolve to the
// same label, e.g. two different "Other" sources). Returns
// [[label, [{code,name}, ...]], ...] in insertion order; callers sort as
// needed for their own presentation.
export function groupCapabilitiesByFriendlyLabel(capabilities, capByCode) {
  const byLabel = new Map();
  for (const code of capabilities || []) {
    const def = capByCode.get(code);
    const label = groupLabel(def?.group);
    if (!byLabel.has(label)) byLabel.set(label, []);
    byLabel.get(label).push({ code, name: def?.name || code });
  }
  return Array.from(byLabel.entries());
}

const userCountLabel = (count) => (count === 1 ? "1 user assigned" : `${count ?? 0} users assigned`);

// Fallback for a 2-line clamp when the repo's Tailwind build has no
// `line-clamp` utility available (this project's tailwind.config.js has an
// empty `plugins: []` — no @tailwindcss/line-clamp) — the standard
// -webkit-box clamp works across the evergreen browsers this app targets.
const CLAMP_2_LINES_STYLE = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

// The template card's one primary action. Compact sibling of
// TemplatePickerSheet's detail-level footer button (same token, smaller
// geometry), and carries `border-0 box-border` for the same reason it
// carries them there — the global `button { all: unset }` reset strips
// box-sizing and border-style, so Tailwind's border utilities don't render
// without them.
const PRIMARY_ACTION_CLS =
  "inline-flex items-center rounded-ctrl border-0 box-border bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-hover cursor-pointer";

// One role card for the Platform tab and TemplatePickerSheet's template grid
// (Organisation roles render as table rows instead — see RolesCatalogPage).
// Two variants:
//
// - "template" (default): a fixed-height, read-only persona card for a
//   system role that seeds new organisation roles via cloning. Carries only
//   what distinguishes one template from another — name, purpose, intended
//   audience, a two-axis classification ("Core · Elevated") and
//   capability/area counts. Everything that read the same on all
//   seven cards was removed: the "Managed template · Not assigned" lock line
//   is now stated once by TemplatePickerSheet's intro copy above the grid,
//   and the per-group capability breakdown lives in the picker's detail
//   level (opened via onViewPermissions) with the full list. Two actions,
//   only one of them primary.
// - "platform": a locked, visually muted card for a directly-assignable
//   platform role (tenant_owner, security_admin, ...). No clone affordance
//   — platform roles can't be cloned from the catalog. Also carries a
//   recommendation chip when the role has `recommended_for` tags (e.g.
//   security_admin's "Recommended alternative to Tenant Owner") — previously
//   seeded on the backend but never surfaced here.
export default function RoleCard({
  role,
  capabilityCatalog = [],
  canManage = false,
  variant = "template",
  onManageAssignments,
  onViewPermissions,
}) {
  const navigate = useNavigate();

  const capByCode = useMemo(() => {
    const m = new Map();
    for (const c of capabilityCatalog) m.set(c.code, c);
    return m;
  }, [capabilityCatalog]);

  const capCount = (role.capabilities || []).length;
  const chips = recommendationChips(role.recommended_for);

  if (variant === "platform") {
    return (
      <div className="rounded-card border border-card-bd bg-surface-2 p-5 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-text truncate">{role.name}</div>
            {role.purpose && <p className="mt-1 text-xs text-text-2">{role.purpose}</p>}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
            <Badge status="neutral" label="Locked" size="sm" />
            {chips[0] && <Badge status="info" label={RECOMMENDATION_COPY[chips[0]]} size="sm" />}
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-xs text-text-3">
          <FontAwesomeIcon icon={icon({ name: "lock", family: "sharp", style: "solid" })} className="text-[10px]" />
          <span>Locked · {userCountLabel(role.assigned_user_count)}</span>
        </div>

        <div className="pt-2 mt-1 border-t border-border flex items-center gap-4">
          <button
            type="button"
            onClick={() =>
              onManageAssignments ? onManageAssignments(role) : navigate(`/settings/users?role=${role.code}`)
            }
            className="text-xs font-medium text-primary hover:text-primary-hover cursor-pointer"
          >
            Manage assignments
          </button>
          <button
            type="button"
            onClick={() => navigate(`/settings/roles/${role.id}`)}
            className="text-xs font-medium text-text-2 hover:text-text cursor-pointer"
          >
            View access
          </button>
        </div>
      </div>
    );
  }

  // Template persona card — fixed height (min-h + flex-col + actions pinned
  // via mt-auto) so every card in the grid lines up regardless of how much
  // (or little) purpose text it carries.
  const classification = templateClassificationBadge(role);
  const audience = AUDIENCE_BY_CODE[role.code];
  const areaCount = groupCapabilitiesByFriendlyLabel(role.capabilities, capByCode).length;

  return (
    <div className="rounded-card border border-card-bd bg-card p-5 flex flex-col gap-3 min-h-[220px]">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-text truncate">{role.name}</div>
        {role.purpose && (
          <p className="mt-1 text-xs text-text-2" style={CLAMP_2_LINES_STYLE}>
            {role.purpose}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {audience && <span className="text-xs text-text-2">{audience}</span>}
        <Badge status={classification.status} label={classification.label} size="sm" />
      </div>

      <div className="text-xs text-text-3">
        {capCount === 1 ? "1 capability" : `${capCount} capabilities`} ·{" "}
        {areaCount === 1 ? "1 area" : `${areaCount} areas`}
      </div>

      <div className="pt-3 mt-auto border-t border-border flex items-center gap-3">
        {canManage && (
          <button
            type="button"
            onClick={() => navigate("/settings/roles/new", { state: { cloneFrom: role.code } })}
            className={PRIMARY_ACTION_CLS}
          >
            Use this template
          </button>
        )}
        <button
          type="button"
          onClick={() => onViewPermissions?.(role)}
          className="text-xs font-medium text-text-2 hover:text-text cursor-pointer"
        >
          View details
        </button>
      </div>
    </div>
  );
}

RoleCard.propTypes = {
  role: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    code: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    purpose: PropTypes.string,
    risk: PropTypes.string,
    is_system: PropTypes.bool,
    capabilities: PropTypes.arrayOf(PropTypes.string),
    assigned_user_count: PropTypes.number,
    recommended_for: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  capabilityCatalog: PropTypes.arrayOf(
    PropTypes.shape({ code: PropTypes.string, name: PropTypes.string, group: PropTypes.string })
  ),
  canManage: PropTypes.bool,
  variant: PropTypes.oneOf(["template", "platform"]),
  onManageAssignments: PropTypes.func,
  onViewPermissions: PropTypes.func,
};
